/**
 *  @class
 *  @function ImageTextOverlay
 */

if (!customElements.get('image-with-text-overlay')) {
  class ImageTextOverlay extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
      this.discountStateTimers = [];
      this.discountVerificationPromise = null;
      this.stateAnimationTimers = new WeakMap();
      this.boundDiscountClick = this.onDiscountActivatorClick.bind(this);
      this.boundDiscountChange = this.onDiscountChange.bind(this);
      this.boundPageShow = this.onPageShow.bind(this);
      this.boundCodeLink = this.onCodeLink.bind(this);
    }

    connectedCallback() {
      this.initializeDiscountActivator();
      document.addEventListener('iwto:discount:changed', this.boundDiscountChange);
      window.addEventListener('pageshow', this.boundPageShow);
      window.addEventListener('co2.codelink', this.boundCodeLink);

      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.prepareAnimations();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('iwto:discount:changed', this.boundDiscountChange);
      window.removeEventListener('pageshow', this.boundPageShow);
      window.removeEventListener('co2.codelink', this.boundCodeLink);
      this.clearDiscountStateTimers();
      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        button.removeEventListener('click', this.boundDiscountClick);
      });

      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        if (this.tl) {
          this.tl.kill();
        }
        if (this.splittext) {
          this.splittext.revert();
        }
      }
    }

    initializeDiscountActivator() {
      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        button.removeEventListener('click', this.boundDiscountClick);
        button.addEventListener('click', this.boundDiscountClick);
      });

      this.syncDiscountActivatorState(true);
    }

    onDiscountChange(event) {
      if (event.detail?.code) {
        this.updateDiscountActivatorState(this.getActiveDiscountCodes() || event.detail.code);
        this.queueDiscountStateChecks([400, 900, 1500]);
        this.scheduleDiscountVerification([700, 1500]);
        return;
      }

      this.syncDiscountActivatorState(true);
    }

    onPageShow() {
      this.syncDiscountActivatorState(true);
    }

    onCodeLink() {
      this.syncDiscountActivatorState();
    }

    async onDiscountActivatorClick(event) {
      const button = event.currentTarget;
      const discountCode = button.dataset.iwtoDiscountCode?.trim();

      if (!discountCode || button.disabled || button.closest('.iwto-discount-activator')?.classList.contains('is-loading')) {
        return;
      }

      const activator = button.closest('.iwto-discount-activator');
      this.setDiscountFeedback(activator, '', true);
      activator?.classList.remove('has-error');
      activator?.classList.add('is-loading');

      try {
        const normalizedCode = discountCode.toUpperCase();
        await this.applyDiscountCode(normalizedCode);

        this.setCookie('discount_code', normalizedCode, 30);
        this.setStoredDiscountCode(normalizedCode);
        this.refreshDiscountDisplays();
        this.notifyDiscountIntegrations(normalizedCode);
        document.dispatchEvent(
          new CustomEvent('iwto:discount:changed', {
            detail: { code: normalizedCode }
          })
        );
      } catch (error) {
        console.error('Error applying image-with-text-overlay discount code:', error);
        activator?.classList.add('has-error');
        this.setDiscountFeedback(
          activator,
          'Code konnte nicht aktiviert werden.',
          false
        );
      } finally {
        activator?.classList.remove('is-loading');
        this.syncDiscountActivatorState();
      }
    }

    async applyDiscountCode(discountCode) {
      if (typeof co2 !== 'undefined' && typeof co2.setDiscountCode === 'function') {
        const applied = await co2.setDiscountCode(discountCode);

        if (!applied) {
          throw new Error(`Failed to apply Datora discount code: ${discountCode}`);
        }

        return;
      }

      const response = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Failed to apply discount code: ${discountCode}`);
      }
    }

    refreshDiscountDisplays() {
      if (typeof window.updateDatoraDiscounts === 'function') {
        window.updateDatoraDiscounts();
      }
    }

    notifyDiscountIntegrations(discountCode) {
      if (typeof co2 !== 'undefined' && typeof co2.getDiscountCode === 'function') {
        window.dispatchEvent(
          new CustomEvent('co2.codelink', {
            detail: { code: discountCode, source: 'image-with-text-overlay' }
          })
        );
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: false, source: 'image-with-text-overlay', code: discountCode }
        })
      );
    }

    syncDiscountActivatorState(syncFromCart = false) {
      this.updateDiscountActivatorState(this.getActiveDiscountCodes());
      this.queueDiscountStateChecks();

      if (syncFromCart) {
        this.queueDiscountStateChecks([400, 900, 1500]);
        this.scheduleDiscountVerification([700, 1500]);
      }
    }

    queueDiscountStateChecks(delays = [250, 900]) {
      this.clearDiscountStateTimers();

      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.updateDiscountActivatorState(this.getActiveDiscountCodes());
        }, delay);

        this.discountStateTimers.push(timerId);
      });
    }

    clearDiscountStateTimers() {
      this.discountStateTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      this.discountStateTimers = [];
    }

    scheduleDiscountVerification(delays = [700]) {
      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.verifyPersistedDiscountState();
        }, delay);

        this.discountStateTimers.push(timerId);
      });
    }

    updateDiscountActivatorState(activeDiscountCodes) {
      const codes = (activeDiscountCodes || '')
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean);

      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        const activator = button.closest('.iwto-discount-activator');
        const label = activator?.querySelector('.iwto-discount-activator__label');
        const buttonText = button.querySelector('.iwto-discount-activator__button-text');
        const icon = button.querySelector('.iwto-discount-activator__icon');
        const code = button.dataset.iwtoDiscountCode?.trim().toUpperCase();
        const isActive = Boolean(code) && codes.includes(code);
        const isAnyActive = codes.length > 0;
        const nextState = isAnyActive ? 'active' : 'inactive';
        const previousState = activator?.dataset.iwtoState;
        const defaultActivatorLabel = activator?.dataset.iwtoLabelDefault || '';
        const activeActivatorLabel = activator?.dataset.iwtoLabelActive !== undefined ? activator.dataset.iwtoLabelActive : defaultActivatorLabel;
        const defaultLabel = button.dataset.iwtoDiscountDefaultLabel || code;

        if (activator && previousState && previousState !== nextState) {
          this.animateActivatorStateChange(activator);
        }

        button.disabled = isAnyActive;
        button.setAttribute('aria-pressed', isAnyActive ? 'true' : 'false');

        if (label) {
          label.textContent = isAnyActive ? activeActivatorLabel : defaultActivatorLabel;
          label.hidden = isAnyActive && activeActivatorLabel === '';
        }

        if (buttonText) {
          buttonText.textContent = isAnyActive ? codes[0] : defaultLabel;
          buttonText.hidden = isAnyActive && activeActivatorLabel === '';
        }

        if (icon) {
          icon.hidden = isAnyActive;
        }

        if (activator) {
          activator.classList.toggle('is-active', isAnyActive);
          activator.dataset.iwtoState = nextState;
          if (isAnyActive && activeActivatorLabel === '') {
            activator.style.display = 'none';
          } else {
            activator.style.display = '';
          }
        }

        if (isActive) {
          activator?.classList.remove('has-error');
          this.setDiscountFeedback(activator, '', true);
        }
      });
    }

    setDiscountFeedback(activator, message, hidden) {
      const feedback = activator?.querySelector('.iwto-discount-activator__feedback');

      if (!feedback) {
        return;
      }

      feedback.textContent = message;
      feedback.hidden = hidden;
    }

    animateActivatorStateChange(activator) {
      const existingTimer = this.stateAnimationTimers.get(activator);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      activator.classList.add('is-updating');

      const timerId = window.setTimeout(() => {
        activator.classList.remove('is-updating');
        this.stateAnimationTimers.delete(activator);
      }, 240);

      this.stateAnimationTimers.set(activator, timerId);
    }

    async verifyPersistedDiscountState() {
      if (this.discountVerificationPromise) {
        return this.discountVerificationPromise;
      }

      this.discountVerificationPromise = (async () => {
        const confirmedCodes = await this.getConfirmedDiscountCodes();

        if (confirmedCodes) {
          const normalizedCodes = this.normalizeCodes(confirmedCodes);

          this.setCookie('discount_code', normalizedCodes.join(','), 30);
          this.setStoredDiscountCode(normalizedCodes[0] || null);
          this.updateDiscountActivatorState(normalizedCodes.join(','));
          return normalizedCodes.join(',');
        }

        this.clearDiscountPersistence();
        this.updateDiscountActivatorState('');
        return '';
      })().finally(() => {
        this.discountVerificationPromise = null;
      });

      return this.discountVerificationPromise;
    }

    async getConfirmedDiscountCodes() {
      const co2Codes = await this.getDiscountCodesFromCo2();
      if (co2Codes.length) {
        return co2Codes.join(',');
      }

      const cartCodes = await this.getDiscountCodesFromCart();
      if (cartCodes.length) {
        return cartCodes.join(',');
      }

      return '';
    }

    async getDiscountCodesFromCo2() {
      if (typeof co2 === 'undefined' || typeof co2.getDiscountCode !== 'function') {
        return [];
      }

      try {
        const currentCode = await co2.getDiscountCode();
        const normalizedCode = this.normalizeCode(currentCode);
        return normalizedCode ? [normalizedCode] : [];
      } catch (error) {
        return [];
      }
    }

    async getDiscountCodesFromCart() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          return [];
        }

        const cart = await response.json();
        const discountCodes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];

        return discountCodes
          .map((discount) => this.normalizeCode(discount?.code || discount))
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    getActiveDiscountCodes() {
      const cookieCodes = this.normalizeCodes(this.getCookie('discount_code'));
      const storedCode = this.normalizeCode(this.getStoredDiscountCode());

      if (!storedCode) {
        return cookieCodes.join(',');
      }

      if (cookieCodes.includes(storedCode)) {
        return cookieCodes.join(',');
      }

      return [...cookieCodes, storedCode].filter(Boolean).join(',');
    }

    normalizeCode(code) {
      return code?.trim().toUpperCase() || '';
    }

    normalizeCodes(codes) {
      return (codes || '')
        .split(',')
        .map((code) => this.normalizeCode(code))
        .filter(Boolean);
    }

    getStoredDiscountCode() {
      try {
        return sessionStorage.getItem('lastDiscountCode');
      } catch (error) {
        return null;
      }
    }

    setStoredDiscountCode(code) {
      try {
        if (code) {
          sessionStorage.setItem('lastDiscountCode', this.normalizeCode(code));
        } else {
          sessionStorage.removeItem('lastDiscountCode');
        }
      } catch (error) {
        // Ignore storage failures in restricted browsers.
      }
    }

    clearDiscountPersistence() {
      this.setCookie('discount_code', '', -1);
      this.setStoredDiscountCode(null);
    }

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);

      if (parts.length === 2) {
        const cookieValue = parts.pop().split(';').shift();
        return cookieValue && cookieValue !== 'null' && cookieValue !== 'undefined'
          ? cookieValue
          : null;
      }

      return null;
    }

    setCookie(name, value, days) {
      let expires = '';

      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
      }

      document.cookie = `${name}=${value || ''}${expires}; path=/`;
    }

    prepareAnimations() {
      let section = this,
        button_offset = 0,
        property =
          gsap.getProperty('html', '--header-height') +
          gsap.getProperty('html', '--header-offset') +
          'px';

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top center'
        }
      });

      document.fonts.ready.then(function () {
        section.splittext = new SplitText(section.querySelectorAll('h3, p:not(.subheading)'), {
          type: 'lines, words',
          linesClass: 'line-child'
        });

        if (section.querySelector('.inline-badge')) {
          section.tl.fromTo(
            section.querySelector('.inline-badge'),
            {
              opacity: 0
            },
            {
              duration: 0.75,
              opacity: 1
            },
            0
          );

          button_offset += 0.5;
        }
        if (section.querySelector('.subheading')) {
          section.tl.fromTo(
            section.querySelector('.subheading'),
            {
              opacity: 0
            },
            {
              duration: 0.75,
              opacity: 0.6
            },
            0
          );

          button_offset += 0.5;
        }
        if (section.querySelector('h3')) {
          let h3_duration =
            0.8 + (section.querySelectorAll('h3 .line-child div').length - 1) * 0.08;
          section.tl
            .set(
              section.querySelector('h3'),
              {
                visibility: 'visible'
              },
              0
            )
            .from(
              section.querySelectorAll('h3 .line-child div'),
              {
                duration: h3_duration,
                yPercent: '100',
                stagger: 0.08
              },
              0
            );
          button_offset += h3_duration;
        }
        if (section.querySelector('.rte p')) {
          let p_duration =
            0.8 + (section.querySelectorAll('.rte p .line-child div').length - 1) * 0.02;
          section.tl
            .set(
              section.querySelectorAll('.rte p'),
              {
                visibility: 'visible'
              },
              0
            )
            .from(
              section.querySelectorAll('.rte p .line-child div'),
              {
                duration: p_duration,
                yPercent: '100',
                stagger: 0.02
              },
              0
            );
          button_offset += p_duration;
        }
        if (section.querySelectorAll('.button')) {
          let i = 1;
          section.querySelectorAll('.button').forEach((item) => {
            section.tl.fromTo(
              item,
              {
                autoAlpha: 0
              },
              {
                duration: 0.5,
                autoAlpha: 1
              },
              button_offset * 0.4 + (i - 1) * 0.1
            );
            i++;
          });
        }
      });

      if (section.querySelector('.thb-parallax-image')) {
        gsap.fromTo(
          section.querySelectorAll('.thb-parallax-image'),
          {
            y: '-8%'
          },
          {
            y: '8%',
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              scrub: 1,
              start: () => `top bottom`,
              end: () => `bottom top+=${property}`,
              onUpdate: () => {
                property =
                  gsap.getProperty('html', '--header-height') +
                  gsap.getProperty('html', '--header-offset') +
                  'px';
              }
            }
          }
        );
      }
    }
  }

  customElements.define('image-with-text-overlay', ImageTextOverlay);
}
